import {
  Avatar,
  BasicDataGrid,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  IconName,
  NestedDataCardProps,
  MenuAction,
  SingleStatusChip,
} from "@inductiveautomation/ws-components";

import { getEmailCardListStyles, getProfileStyles } from "./_profile.styles";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  AdditionalEmail,
  OtherEmailCardsProps,
  PrimaryEmailCardProps,
} from "./Profile.types";
import { StatefulEmailAddress } from "./api/profile.api";

const PrimaryEmailIcon = () => (
  <Avatar iconAvatarColor="primary" icon="email" />
);

const NeutralEmailIcon = () => {
  const { classes } = getProfileStyles();

  return (
    <Avatar
      iconAvatarColor="neutral"
      icon="email"
      className={classes.neutralEmailIcon}
    />
  );
};

const getLabelForEmailStatus = (
  status: StatefulEmailAddress["status"],
  t: TFunction
): string => {
  switch (status) {
    case "UNVERIFIED":
      return t("profile.additional_emails.status.unverified");
    case "PENDING":
      return t("profile.additional_emails.status.pending");
    case "VERIFIED":
      return t("profile.additional_emails.status.verified");
    default:
      return t("profile.additional_emails.status.unverified");
  }
};

const cardsForOtherEmails = (
  otherEmails: AdditionalEmail[],
  actionsFactory: (email: AdditionalEmail) => MenuAction[][],
  emptyHeading: string,
  t: TFunction
): NestedDataCardProps[] => {
  if (otherEmails.length > 0) {
    return [...otherEmails].sort().map(({ email, status }) => ({
      id: `other-email-card-${email}`,
      contentColumns: [
        createDataCardInitialColumnDefinition({
          parentTestId: `other-email-card-${email}`,
          heading: email,
          avatar: <PrimaryEmailIcon />,
        }),
        createInfoCardColumnDefinition(
          <SingleStatusChip
            testId="email-status-chip"
            label={getLabelForEmailStatus(status, t)}
          />,
          96,
          100
        ),
      ],
      noninteractive: true,
      actionLists: actionsFactory({ email, status }),
      childItems: [],
    }));
  }

  return [
    {
      id: "no-emails-card",
      contentColumns: [
        createDataCardInitialColumnDefinition({
          parentTestId: "no-emails-card",
          heading: emptyHeading,
          avatar: <NeutralEmailIcon />,
        }),
        createInfoCardColumnDefinition(<></>),
      ],
      noninteractive: true,
      disabled: true,
      childItems: [],
    },
  ];
};

export const OtherEmailCards = ({
  emails,
  onRemove,
  onResendVerification,
}: OtherEmailCardsProps) => {
  const { t } = useTranslation();
  const { classes } = getEmailCardListStyles();

  const actions = (email: AdditionalEmail): MenuAction[][] => [
    [
      ...(email.status !== "VERIFIED"
        ? [
            {
              id: "resend-verification",
              label: t("profile.additional_emails.resend_action"),
              iconLeft: "email-outlined-notification" as IconName,
              onClick: () => onResendVerification(email.email),
            },
          ]
        : []),
      {
        id: "remove-email",
        label: t("profile.additional_emails.remove_action"),
        iconLeft: "delete-outlined" as IconName,
        iconLeftVariant: "destructive",
        onClick: () => onRemove(email.email),
      },
    ],
  ];

  const otherEmailCards = cardsForOtherEmails(
    emails,
    actions,
    t("profile.additional_emails.no_email_addresses"),
    t
  );

  return (
    <div className={classes.rootWithInfo}>
      <BasicDataGrid
        testId="profile-other-email-addresses"
        enableSorting={false}
        headings={[
          {
            id: "heading-additional-emails",
            label: t("profile.additional_emails.data_grid.heading"),
          },
          {
            id: "heading-status",
            label: "",
          },
        ]}
        data={otherEmailCards}
        labels={{
          singular_item_name: t(
            "profile.additional_emails.data_grid.singular_item_name"
          ),
          plural_item_name: t(
            "profile.additional_emails.data_grid.plural_item_name"
          ),
        }}
      />
    </div>
  );
};

export const PrimaryEmailCard = ({
  email,
  onChangePrimary,
}: PrimaryEmailCardProps) => {
  const { t } = useTranslation();
  const { classes } = getEmailCardListStyles();

  return (
    <div className={classes.rootWithoutInfo}>
      <BasicDataGrid
        testId="profile-primary-email-address"
        enableSorting={false}
        headings={[
          {
            id: "heading-primary-email",
            label: t("profile.primary_email.data_grid.heading"),
          },
        ]}
        labels={{
          singular_item_name: t(
            "profile.primary_email.data_grid.singular_item_name"
          ),
          plural_item_name: t(
            "profile.primary_email.data_grid.plural_item_name"
          ),
        }}
        data={[
          {
            id: "primary-email-card",
            contentColumns: [
              createDataCardInitialColumnDefinition({
                parentTestId: "primary-email-card",
                heading: email,
                avatar: <PrimaryEmailIcon />,
              }),
            ],
            noninteractive: true,
            actionLists: [
              [
                {
                  id: "change-primary-email",
                  label: t("profile.primary_email.actions.change.label"),
                  iconLeft: "email-outlined-check",
                  onClick: () => onChangePrimary(),
                },
              ],
            ],
            childItems: [],
          },
        ]}
      />
    </div>
  );
};
